import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, ActivityIndicator } from 'react-native';
import { X, AlertTriangle, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient } from '../lib/api/client';
import { NETFLIX_COLORS, NETFLIX_SPACING } from '../theme/netflixTheme';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  seriesId: string | undefined;
  episodeNumber: number;
}

const REPORT_REASONS = [
  { id: 'video_not_loading', label: '🎬 Vídeo não carrega', emoji: '🎬' },
  { id: 'video_freezes', label: '❄️ Vídeo congela/trava', emoji: '❄️' },
  { id: 'no_sound', label: '🔇 Som não funciona', emoji: '🔇' },
  { id: 'no_subtitles', label: '📝 Legenda faltando', emoji: '📝' },
  { id: 'bad_quality', label: '📉 Qualidade muito baixa', emoji: '📉' },
  { id: 'expired_link', label: '⏰ Link expirado/inválido', emoji: '⏰' },
  { id: 'wrong_content', label: '⚠️ Conteúdo inadequado', emoji: '⚠️' },
  { id: 'other', label: '🤔 Outro problema', emoji: '🤔' },
];

export function ReportModal({ isOpen, onClose, seriesId, episodeNumber }: ReportModalProps) {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedReason) {
      toast.error('Selecione um motivo');
      return;
    }

    if (!seriesId) {
      toast.error('Série não identificada');
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient.post('/streaming/reports', {
        series_id: seriesId,
        episode_number: episodeNumber,
        reason: selectedReason,
        description: description.trim() || null,
        user_agent: navigator.userAgent,
      });

      toast.success('✅ Obrigado! Vamos investigar isso');
      setSelectedReason(null);
      setDescription('');
      onClose();
    } catch (error) {
      console.error('Erro ao enviar denúncia:', error);
      toast.error('❌ Erro ao enviar denúncia');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <View style={styles.overlay}>
      <View style={styles.container}>
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerTitle}>
              <AlertTriangle size={24} color="#ef4444" style={styles.alertIcon} />
              <Text style={styles.title}>Reportar Problema</Text>
            </View>
            <TouchableOpacity
              onPress={onClose}
              disabled={isSubmitting}
              style={styles.closeButton}
            >
              <X size={24} color={NETFLIX_COLORS.textSecondary} />
            </TouchableOpacity>
          </View>

          {/* Description */}
          <Text style={styles.description}>
            Ajude-nos a melhorar! Deixe-nos saber qual é o problema:
          </Text>

          {/* Reason Selection */}
          <View style={styles.reasonsContainer}>
            {REPORT_REASONS.map((reason) => (
              <TouchableOpacity
                key={reason.id}
                style={[
                  styles.reasonButton,
                  selectedReason === reason.id && styles.reasonButtonSelected,
                ]}
                onPress={() => setSelectedReason(reason.id)}
                disabled={isSubmitting}
              >
                <Text style={styles.reasonEmoji}>{reason.emoji}</Text>
                <Text style={styles.reasonLabel}>{reason.label}</Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Description (optional) */}
          {selectedReason === 'other' && (
            <View style={styles.descriptionFieldContainer}>
              <Text style={styles.descriptionLabel}>
                Descreva o problema (opcional):
              </Text>
              <textarea
                style={descriptionInputStyle}
                placeholder="Ex: O vídeo começou a travar após 5 minutos..."
                value={description}
                onChange={(e) => setDescription((e.target as any).value)}
                disabled={isSubmitting}
              />
            </View>
          )}

          {/* Actions */}
          <View style={styles.actions}>
            <TouchableOpacity
              style={[styles.button, styles.cancelButton, isSubmitting && styles.buttonDisabled]}
              onPress={onClose}
              disabled={isSubmitting}
            >
              <Text style={styles.buttonText}>Cancelar</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.button,
                styles.submitButton,
                (isSubmitting || !selectedReason) && styles.buttonDisabled,
              ]}
              onPress={handleSubmit}
              disabled={isSubmitting || !selectedReason}
            >
              {isSubmitting ? (
                <ActivityIndicator color="white" size="small" />
              ) : (
                <>
                  <Send size={16} color="white" style={styles.sendIcon} />
                  <Text style={styles.buttonText}>Reportar</Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>
    </View>
  );
}

const descriptionInputStyle = {
  width: '100%',
  backgroundColor: NETFLIX_COLORS.surface,
  borderRadius: '8px',
  paddingLeft: `${NETFLIX_SPACING.md}px`,
  paddingRight: `${NETFLIX_SPACING.md}px`,
  paddingTop: `${NETFLIX_SPACING.sm}px`,
  paddingBottom: `${NETFLIX_SPACING.sm}px`,
  color: NETFLIX_COLORS.white,
  fontSize: '14px',
  border: `1px solid ${NETFLIX_COLORS.border}`,
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  minHeight: '100px',
  textAlignVertical: 'top',
  resize: 'vertical',
} as any;

const styles = StyleSheet.create({
  overlay: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  } as any,
  container: {
    width: '90%',
    maxWidth: 500,
    maxHeight: '90vh',
    backgroundColor: NETFLIX_COLORS.surfaceHover,
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
  } as any,
  scrollContent: {
    padding: NETFLIX_SPACING.lg,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: NETFLIX_SPACING.lg,
  },
  headerTitle: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: NETFLIX_COLORS.white,
    marginLeft: NETFLIX_SPACING.sm,
  },
  closeButton: {
    padding: NETFLIX_SPACING.sm,
    marginLeft: NETFLIX_SPACING.md,
  },
  description: {
    fontSize: 14,
    color: NETFLIX_COLORS.textSecondary,
    marginBottom: NETFLIX_SPACING.lg,
    lineHeight: 20,
  },
  reasonsContainer: {
    gap: NETFLIX_SPACING.sm,
    marginBottom: NETFLIX_SPACING.lg,
  },
  reasonButton: {
    width: '100%',
    backgroundColor: NETFLIX_COLORS.surface,
    borderRadius: 8,
    paddingHorizontal: NETFLIX_SPACING.md,
    paddingVertical: NETFLIX_SPACING.sm,
    flexDirection: 'row',
    alignItems: 'center',
    gap: NETFLIX_SPACING.sm,
    borderWidth: 1,
    borderColor: NETFLIX_COLORS.border,
  },
  reasonButtonSelected: {
    backgroundColor: '#dc2626',
    borderColor: '#dc2626',
  },
  reasonEmoji: {
    fontSize: 18,
  },
  reasonLabel: {
    color: NETFLIX_COLORS.textPrimary,
    fontSize: 14,
    fontWeight: '500',
  },
  descriptionFieldContainer: {
    marginBottom: NETFLIX_SPACING.lg,
  },
  descriptionLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: NETFLIX_COLORS.textSecondary,
    marginBottom: NETFLIX_SPACING.sm,
  },
  actions: {
    flexDirection: 'row',
    gap: NETFLIX_SPACING.md,
    marginTop: NETFLIX_SPACING.lg,
  },
  button: {
    flex: 1,
    borderRadius: 8,
    paddingVertical: NETFLIX_SPACING.md,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  cancelButton: {
    backgroundColor: NETFLIX_COLORS.surface,
    borderWidth: 1,
    borderColor: NETFLIX_COLORS.border,
  },
  submitButton: {
    backgroundColor: '#dc2626',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: '700',
    color: NETFLIX_COLORS.white,
  },
  alertIcon: {
    marginRight: 8,
  },
  sendIcon: {
    marginRight: 6,
  },
});
